<template>
  <div v-if="session._id" class="session-detail">
    <div class="page-control__button" @click="goBack">
      <span>← Back</span>
    </div>
    <div class="session-detail__title">{{ session.subTopic }} session</div>
    <div class="session-detail__subtitle">ID: {{ session._id }}</div>

    <session-flags :flags="session.reviewReasons" />

    <section class="session-detail__section" v-if="session.toReview">
      <h3 class="session-detail__section-title">Review Session</h3>
      <p v-if="reviewError" class="error">
        There was an issue submitting your review for this session.
      </p>

      <div class="uc-form-checkbox">
        <input
          id="session-detail__review"
          type="checkbox"
          @click="toggleReviewedSession($event)"
        />
        <label
          class="session-detail__review-label"
          for="session-detail__review"
        >
          Reviewed session
        </label>
      </div>
    </section>

    <separator class="separator" />

    <div v-if="session.student" class="session-detail__section">
      <div class="session-detail__section-title">Student</div>
      <user-preview :user="session.student" />
    </div>
    <div v-if="session.volunteer" class="session-detail__section">
      <div class="session-detail__section-title">Volunteer</div>
      <user-preview :user="session.volunteer" />
    </div>
    <div class="session-detail__section">
      <div class="session-detail__section">
        <div class="session-detail__section-title">Started</div>
        <div>{{ createdAt }}</div>
        <div v-if="session.userAgent" class="session-detail__section--device">
          <div>{{ devicePlatform }} - {{ session.userAgent.device }}</div>
          <div>
            {{ session.userAgent.browser }}:
            {{ session.userAgent.browserVersion }}
          </div>
          <div>
            {{ session.userAgent.operatingSystem }}:
            {{ session.userAgent.operatingSystemVersion }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="session.volunteer" class="session-detail__section">
      <div class="session-detail__section-title">Volunteer joined</div>
      <div>{{ volunteerJoinedAt }}</div>
    </div>
    <div v-if="endedAt" class="session-detail__section">
      <div class="session-detail__section-title">Ended by {{ endedBy }}</div>
      <div>{{ endedAt }}</div>
    </div>
    <div class="session-detail__section">
      <div class="session-detail__section-title">
        Notifications
      </div>
      <router-link
        :to="`/${$i18n.locale}/admin/sessions/${session._id}/notifications`"
        >{{
          session.notifications ? session.notifications.length : 0
        }}
        sent</router-link
      >
      <div v-if="session.reportReason" class="session-detail__section">
        <div class="session-detail__section-title">Report Reason</div>
        <div>{{ session.reportReason }}</div>
      </div>
      <div v-if="session.reportMessage" class="session-detail__section">
        <div class="session-detail__section-title">Report Message</div>
        <div>{{ session.reportMessage }}</div>
      </div>
    </div>
    <div
      v-if="session.messages.length"
      class="session-detail__section session-detail__section--chat"
    >
      <div class="session-detail__section-title">
        Chat log ({{ session.messages.length }})
      </div>
      <chat-log
        :messages="session.messages"
        :student="session.student"
        :volunteer="session.volunteer"
      />
    </div>
    <div
      v-if="legacyStudentFeedback"
      class="session-detail__section session-detail__section--feedback"
    >
      <div class="session-detail__section-title">
        Student feedback
      </div>
      <feedback-preview
        :feedback="legacyStudentFeedback"
        :userType="'student'"
      />
    </div>
    <div
      v-if="legacyVolunteerFeedback"
      class="session-detail__section session-detail__section--feedback"
    >
      <div class="session-detail__section-title">
        Volunteer feedback
      </div>
      <feedback-preview
        :feedback="legacyVolunteerFeedback"
        :userType="'volunteer'"
      />
    </div>
    <div
      v-if="presessionSurvey"
      class="session-detail__section session-detail__section--feedback"
    >
      <div class="session-detail__section-title">
        Student Presession Survey
      </div>
      <survey-response-preview :response="presessionSurvey" />
    </div>
    <div
      v-if="studentPostsessionSurvey"
      class="session-detail__section session-detail__section--feedback"
    >
      <div class="session-detail__section-title">
        Student Postsession Survey
      </div>
      <survey-response-preview :response="studentPostsessionSurvey" />
    </div>
    <div
      v-if="volunteerPostsessionSurvey"
      class="session-detail__section session-detail__section--feedback"
    >
      <div class="session-detail__section-title">
        Volunteer Postsession Survey
      </div>
      <survey-response-preview :response="volunteerPostsessionSurvey" />
    </div>
    <div v-if="session.photos.length > 0" class="session-detail__section">
      <div class="session-detail__section-title">Photos</div>
      <img
        v-for="(image, index) in session.photos"
        :key="index"
        :src="image"
        class="session-detail__section--image"
        alt="a photo posted by student in the session"
      />
    </div>
    <div
      v-if="isDocEditorSession"
      class="session-detail__section session-detail__section--document"
    >
      <h2 class="session-detail__section-title">Document</h2>
      <div class="quill-container"></div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import NetworkService from '@/services/NetworkService'
import UserPreview from '@/components/Admin/UserPreview.vue'
import ChatLog from '@/components/Admin/ChatLog/index.vue'
import FeedbackPreview from '@/components/Admin/FeedbackPreview.vue'
import SurveyResponsePreview from '@/components/Admin/SurveyResponsePreview.vue'
import Quill from 'quill'
import SessionFlags from '@/components/Admin/SessionFlags.vue'
import Separator from '@/components/Separator.vue'
import { SESSION_TOOL_TYPES } from '@/consts'

export default {
  name: 'AdminSessionDetail',

  components: {
    UserPreview,
    ChatLog,
    FeedbackPreview,
    SurveyResponsePreview,
    SessionFlags,
    Separator,
  },

  data() {
    return {
      session: {},
      quillEditor: null,
      reviewError: false,
    }
  },

  computed: {
    createdAt() {
      return moment(this.session.createdAt).format('l, h:mm:ss a')
    },
    volunteerJoinedAt() {
      return moment(this.session.volunteerJoinedAt).format('l, h:mm:ss a')
    },
    endedAt() {
      if (!this.session.endedAt) return null
      return moment(this.session.endedAt).format('l, h:mm:ss a')
    },
    endedBy() {
      if (this.session.endedBy === null) return 'worker'
      const endedBy =
        this.session.endedBy === this.session.student._id
          ? this.session.student
          : this.session.volunteer
      return endedBy ? endedBy.firstname : '?'
    },
    // pre-context sharing feedback
    legacyStudentFeedback() {
      return this.session.feedbacks &&
        (this.session.feedbacks.studentTutoringFeedback ||
          this.session.feedbacks.studentCounselingFeedback)
        ? this.session.feedbacks
        : null
    },
    // pre-context sharing feedback
    legacyVolunteerFeedback() {
      return this.session.feedbacks && this.session.feedbacks.volunteerFeedback
        ? this.session.feedbacks
        : null
    },
    presessionSurvey() {
      return this.session.surveyResponses &&
        this.session.surveyResponses.presessionSurvey
        ? this.session.surveyResponses.presessionSurvey
        : null
    },
    studentPostsessionSurvey() {
      return this.session.surveyResponses &&
        this.session.surveyResponses.studentPostsessionSurvey
        ? this.session.surveyResponses.studentPostsessionSurvey
        : null
    },
    volunteerPostsessionSurvey() {
      return this.session.surveyResponses &&
        this.session.surveyResponses.volunteerPostsessionSurvey
        ? this.session.surveyResponses.volunteerPostsessionSurvey
        : null
    },
    devicePlatform() {
      if (
        this.session.userAgent.browser === 'Chrome WebView' ||
        this.session.userAgent.browser === 'WebKit'
      )
        return 'Mobile app'

      if (
        this.session.userAgent.device === 'Apple' ||
        this.session.userAgent.operatingSystem === 'Android'
      )
        return 'Mobile web'

      return 'Desktop'
    },
    isDocEditorSession() {
      return this.session.toolType === SESSION_TOOL_TYPES.DOCUMENT_EDITOR
    },
  },

  async created() {
    const {
      data: { session },
    } = await NetworkService.adminGetSession(this.$route.params.sessionId)
    this.session = session

    // Set quill document after the DOM has been updated to show session div
    this.$nextTick(async () => {
      if (this.isDocEditorSession) {
        const container = document.querySelector('.quill-container')
        this.quillEditor = new Quill(container)
        this.quillEditor.enable(false)
        this.quillEditor.setContents(JSON.parse(this.session.quillDoc))
      }
    })
  },

  methods: {
    async toggleReviewedSession(event) {
      const {
        target: { checked },
      } = event
      this.reviewError = false
      const sessionId = this.session._id
      const data = { reviewed: checked, toReview: !checked }

      try {
        await NetworkService.adminUpdateSession(sessionId, data)
      } catch (error) {
        this.reviewError = true
      }
    },

    goBack() {
      this.$router.go(-1)
    },
  },
}
</script>

<style lang="scss" scoped>
.session-detail {
  max-width: 800px;
  background: #fff;
  margin: 10px;
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;

  @include breakpoint-above('medium') {
    padding: 40px;
    margin: 40px;
  }

  &__title {
    text-transform: capitalize;
    font-size: 24px;
    margin-top: 20px;
  }

  &__subtitle {
    color: $c-secondary-grey;
    font-size: 18px;
    margin-bottom: 20px;
  }

  &__section {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 10px 0;
    font-size: 20px;

    &--chat {
      margin: 20px 0 40px;
    }

    &--feedback {
      margin-bottom: 20px;
    }

    &--image {
      width: 60%;
    }

    &--device {
      text-align: left;
      font-size: 14px;
      padding: 5px 10px;
      margin: 5px 0;
      border-left: solid #ececec 5px;
    }

    &--document {
      height: 500px;
      overflow-y: auto;
    }
  }

  &__review-label {
    @include font-category('helper-text');
    font-weight: 600;
  }

  &__section-title {
    color: $c-secondary-grey;
    font-size: 16px;
  }
}

.back-button {
  display: inline-flex;
  color: #417db1;
  border-radius: 20px;
  padding: 5px 15px;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    background: #f7fcfe;
  }
}

.uc-form-checkbox {
  margin: 0.5em 0;
}

.page-control__button {
  display: inline-flex;
  align-items: center;
  color: #417db1;
  border-radius: 20px;
  padding: 5px 15px;
  cursor: pointer;

  &:hover {
    background: #f7fcfe;
  }
}

.separator {
  margin: 2em 0;
}

.error {
  color: $c-error-red;
  margin: 1em 0;
}
</style>
